import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { ResultEntryModal } from '../manage-event-classes/components/result-entry-modal/result-entry-modal';
import { ScoringService } from '../../../../../../../../@shared/services/scoring.service';
import {
  EventScheduleResponse,
  EventScheduleEntryResponse,
  EventScheduleLiveStatusResponse,
  EventClassResponse,
  ClassScoringConfigResponse,
  ClassRoundResponse,
  ClassResultResponse
} from '../../../../../../../../@shared/models/response.module';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-live-session-control',
  standalone: false,
  templateUrl: './live-session-control.html',
  styleUrl: './live-session-control.css'
})
export class LiveSessionControl implements OnInit {
  // Route params
  eventId: number;
  scheduleId: number;

  // Data
  schedule: EventScheduleResponse | null = null;
  eventClass: EventClassResponse | null = null;
  entries: EventScheduleEntryResponse[] = [];
  currentEntry: EventScheduleEntryResponse | null = null;
  nextEntries: EventScheduleEntryResponse[] = [];

  // State
  loading = true;
  processing = false;
  sessionEnded = false;

  // Scoring
  scoringConfig: ClassScoringConfigResponse | null = null;
  rounds: ClassRoundResponse[] = [];
  selectedRound: ClassRoundResponse | null = null;

  // Results Board
  allResults: Map<number, ClassResultResponse> = new Map();
  positions: Map<number, number> = new Map();
  editingCell: { registrationId: number, field: 'time' | 'points' } | null = null;
  editValue: string = '';
  quickEntryValue: string = '';

  // Notes, Image editing
  editingNotesId: number | null = null;
  editNotesValue: string = '';
  currentImageEntry: EventScheduleEntryResponse | null = null;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private scoringService: ScoringService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.eventId = +this.route.snapshot.params['event_id'];
    this.scheduleId = +this.route.snapshot.params['schedule_id'];
    this.loadScheduleData();
  }


  async loadScheduleData() {
    this.loading = true;
    try {
      // Get live status which includes schedule and current/next entry info
      const liveStatus: EventScheduleLiveStatusResponse = await firstValueFrom(
        this.eventsService.getLiveStatus({ schedule_id: this.scheduleId })
      );

      if (liveStatus && liveStatus.schedule) {
        this.schedule = liveStatus.schedule;

        // Get all entries sorted by position (include breaks)
        const entries = await firstValueFrom(
          this.eventsService.getScheduleEntries({ schedule_id: this.scheduleId })
        );
        this.entries = entries
          .filter((e: EventScheduleEntryResponse) => e.entry_type === 'participant' || e.entry_type === 'break')
          .sort((a: EventScheduleEntryResponse, b: EventScheduleEntryResponse) => a.position - b.position);

        // Compute current and next entries locally from entries array
        this.updateCurrentAndNextEntries();

        // Check if session is already completed
        if (this.schedule?.status === 'completed') {
          this.sessionEnded = true;
        }

        // Load scoring config and rounds for the event class if available
        if (this.schedule?.event_class_id) {
          await this.loadScoringData(this.schedule.event_class_id);
        }
      }
    } catch (error) {
      console.error('Error loading schedule data:', error);
      this.toastService.error(this.translateService.instant('liveSession.loadError'));
    } finally {
      this.loading = false;
    }
  }

  private updateCurrentAndNextEntries() {
    const activeEntries = this.entries.filter(e => e.status !== 'scratched');
    const currentPos = this.schedule?.current_entry_position || 1;

    // Find current entry - the entry at current position (or first non-scratched if not found)
    this.currentEntry = activeEntries.find(e => e.position === currentPos) ||
                        activeEntries.find(e => e.position >= currentPos) ||
                        null;

    // Find next 3 entries - non-scratched entries after current position
    if (this.currentEntry) {
      this.nextEntries = activeEntries
        .filter(e => e.position > this.currentEntry!.position)
        .slice(0, 3);
    } else {
      this.nextEntries = [];
    }
  }

  async refreshLiveStatus() {
    if (!this.scheduleId) return;

    try {
      const liveStatus: EventScheduleLiveStatusResponse = await firstValueFrom(
        this.eventsService.getLiveStatus({ schedule_id: this.scheduleId })
      );

      if (liveStatus) {
        this.schedule = liveStatus.schedule;

        // Recompute current and next entries from local entries array
        this.updateCurrentAndNextEntries();

        if (this.schedule?.status === 'completed') {
          this.sessionEnded = true;
        }
      }
    } catch (error) {
      console.error('Error refreshing live status:', error);
    }
  }

  // Navigation
  get currentPosition(): number {
    return this.schedule?.current_entry_position || 1;
  }

  get totalParticipants(): number {
    return this.entries.filter(e => e.entry_type === 'participant' && e.status !== 'scratched').length;
  }

  get totalEntries(): number {
    return this.entries.filter(e => e.status !== 'scratched').length;
  }

  get activeEntries(): EventScheduleEntryResponse[] {
    return this.entries.filter(e => e.status !== 'scratched');
  }

  get progressPercent(): number {
    if (this.totalEntries === 0) return 0;
    // Calculate progress based on current index in active entries
    const currentIndex = this.activeEntries.findIndex(e => e.position === this.currentPosition);
    return Math.round(((currentIndex + 1) / this.totalEntries) * 100);
  }

  get canGoNext(): boolean {
    const currentIndex = this.activeEntries.findIndex(e => e.position === this.currentPosition);
    return currentIndex < this.activeEntries.length - 1 && !this.sessionEnded && !this.processing;
  }

  get isLastEntry(): boolean {
    const currentIndex = this.activeEntries.findIndex(e => e.position === this.currentPosition);
    return currentIndex >= this.activeEntries.length - 1;
  }

  // Keep for backwards compatibility
  get isLastParticipant(): boolean {
    return this.isLastEntry;
  }

  isBreak(entry: EventScheduleEntryResponse | null): boolean {
    return entry?.entry_type === 'break';
  }

  // Actions
  async goNext() {
    if (!this.canGoNext || this.processing) return;

    this.processing = true;
    try {
      // Advance to next participant - backend recalculates all times in single transaction
      await firstValueFrom(
        this.eventsService.advanceSchedule({ schedule_id: this.scheduleId, direction: 'next' })
      );

      // Reload entries to get updated times from backend (include breaks)
      const entries = await firstValueFrom(
        this.eventsService.getScheduleEntries({ schedule_id: this.scheduleId })
      );
      this.entries = entries
        .filter((e: EventScheduleEntryResponse) => e.entry_type === 'participant' || e.entry_type === 'break')
        .sort((a: EventScheduleEntryResponse, b: EventScheduleEntryResponse) => a.position - b.position);

      // Update local state
      this.updateCurrentAndNextEntries();

      // Refresh the live status
      await this.refreshLiveStatus();
    } catch (error) {
      console.error('Error advancing schedule:', error);
      this.toastService.error(this.translateService.instant('liveSession.advanceError'));
    } finally {
      this.processing = false;
    }
  }

  async scratchCurrent() {
    if (!this.currentEntry || this.processing || this.sessionEnded) return;

    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.title = this.translateService.instant('liveSession.scratchConfirmTitle');
    modalRef.componentInstance.message = this.translateService.instant('liveSession.scratchConfirmMessage', {
      name: this.getParticipantName(this.currentEntry)
    });
    modalRef.componentInstance.confirmLabel = this.translateService.instant('liveSession.scratch');

    try {
      const confirmed = await modalRef.result;
      if (confirmed) {
        this.processing = true;

        // Scratch the current entry
        await firstValueFrom(
          this.eventsService.scratchCurrentEntry(this.scheduleId, this.currentEntry.entry_id)
        );

        // Advance to next (if not last)
        if (!this.isLastParticipant) {
          await firstValueFrom(
            this.eventsService.advanceSchedule({ schedule_id: this.scheduleId, direction: 'next' })
          );
        }

        // Reload entries and refresh status (include breaks)
        const entries = await firstValueFrom(
          this.eventsService.getScheduleEntries({ schedule_id: this.scheduleId })
        );
        this.entries = entries
          .filter((e: EventScheduleEntryResponse) => e.entry_type === 'participant' || e.entry_type === 'break')
          .sort((a: EventScheduleEntryResponse, b: EventScheduleEntryResponse) => a.position - b.position);

        await this.refreshLiveStatus();
        this.toastService.success(this.translateService.instant('liveSession.scratchSuccess'));
      }
    } catch (error) {
      if (error !== 0 && error !== 'cancel') {
        console.error('Error scratching entry:', error);
        this.toastService.error(this.translateService.instant('liveSession.scratchError'));
      }
    } finally {
      this.processing = false;
    }
  }

  async endSession() {
    if (this.processing || this.sessionEnded) return;

    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.title = this.translateService.instant('liveSession.endConfirmTitle');
    modalRef.componentInstance.message = this.translateService.instant('liveSession.endConfirmMessage');
    modalRef.componentInstance.confirmLabel = this.translateService.instant('liveSession.endSession');

    try {
      const confirmed = await modalRef.result;
      if (confirmed) {
        this.processing = true;

        await firstValueFrom(
          this.eventsService.endLiveSession(this.scheduleId)
        );

        this.sessionEnded = true;
        this.toastService.success(this.translateService.instant('liveSession.sessionEndedSuccess'));
      }
    } catch (error) {
      if (error !== 0 && error !== 'cancel') {
        console.error('Error ending session:', error);
        this.toastService.error(this.translateService.instant('liveSession.endError'));
      }
    } finally {
      this.processing = false;
    }
  }

  goBack() {
    this.router.navigate(['/company-profile/manage-event', this.eventId], {
      queryParams: { tab: 'schedule' }
    });
  }

  // Scoring methods
  async loadScoringData(eventClassId: number) {
    try {
      // Load scoring config
      try {
        this.scoringConfig = await firstValueFrom(
          this.scoringService.getScoringConfig({ event_class_id: eventClassId })
        );
      } catch (e) {
        this.scoringConfig = null;
      }

      // Load rounds
      try {
        this.rounds = await firstValueFrom(
          this.scoringService.getRounds({ event_class_id: eventClassId })
        ) || [];
        // Select first round by default if available
        if (this.rounds.length > 0) {
          // Prefer in_progress round, then pending, then first
          this.selectedRound = this.rounds.find(r => r.status === 'in_progress') ||
                               this.rounds.find(r => r.status === 'pending') ||
                               this.rounds[0];
          // Load results for selected round
          await this.loadAllResults();
        }
      } catch (e) {
        this.rounds = [];
      }
    } catch (error) {
      console.error('Error loading scoring data:', error);
    }
  }

  selectRound(round: ClassRoundResponse) {
    this.selectedRound = round;
    // Reload results when round changes
    this.loadAllResults();
  }

  async loadAllResults() {
    if (!this.schedule?.event_class_id || !this.selectedRound) return;

    try {
      const results = await firstValueFrom(
        this.scoringService.getResults({
          event_class_id: this.schedule.event_class_id,
          round_id: this.selectedRound.round_id
        })
      );
      this.allResults = new Map(
        (results || []).map((r: ClassResultResponse) => [r.class_registration_id, r])
      );
      this.calculatePositions();
    } catch (error) {
      console.error('Error loading results:', error);
      this.allResults = new Map();
    }
  }

  calculatePositions() {
    // Get participants (not breaks) with recorded results
    const participantsWithResults = this.participantEntries
      .filter(e => {
        const regId = e.registration?.class_registration_id;
        if (!regId) return false;
        const result = this.allResults.get(regId);
        return result && result.status === 'recorded';
      })
      .map(e => ({
        entry: e,
        result: this.allResults.get(e.registration!.class_registration_id)!
      }));

    // Sort based on scoring type
    if (this.scoringConfig?.score_type === 'timed' || this.scoringConfig?.score_type === 'combined') {
      participantsWithResults.sort((a, b) =>
        (a.result.final_time_seconds || Infinity) - (b.result.final_time_seconds || Infinity)
      );
    } else if (this.scoringConfig?.score_type === 'points_high') {
      participantsWithResults.sort((a, b) =>
        (b.result.final_points || 0) - (a.result.final_points || 0)
      );
    } else if (this.scoringConfig?.score_type === 'points_low') {
      participantsWithResults.sort((a, b) =>
        (a.result.final_points || Infinity) - (b.result.final_points || Infinity)
      );
    }

    // Assign positions
    this.positions = new Map();
    participantsWithResults.forEach((p, index) => {
      this.positions.set(p.entry.registration!.class_registration_id, index + 1);
    });
  }

  // Inline editing
  startEdit(entry: EventScheduleEntryResponse, field: 'time' | 'points') {
    if (entry.status === 'scratched' || entry.entry_type === 'break') return;
    if (!entry.registration?.class_registration_id) return;

    // Set initial value based on field
    if (field === 'time') {
      const time = this.getRawTime(entry);
      this.editValue = time ? time.toString() : '';
    } else {
      const points = this.getRawPoints(entry);
      this.editValue = points ? points.toString() : '';
    }

    this.editingCell = {
      registrationId: entry.registration.class_registration_id,
      field
    };

    // Focus the input after DOM updates
    setTimeout(() => {
      const input = document.querySelector('.results-table input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  isEditing(entry: EventScheduleEntryResponse, field: 'time' | 'points'): boolean {
    if (!entry.registration?.class_registration_id) return false;
    return this.editingCell?.registrationId === entry.registration.class_registration_id
        && this.editingCell?.field === field;
  }

  onInputKeydown(event: KeyboardEvent, entry: EventScheduleEntryResponse, field: 'time' | 'points') {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.saveAndMoveToNext(entry, field, this.editValue);
    } else if (event.key === 'Escape') {
      this.editingCell = null;
      this.editValue = '';
    }
  }

  onInputBlur(entry: EventScheduleEntryResponse, field: 'time' | 'points') {
    // Save on blur if there's a value
    if (this.editValue.trim()) {
      this.saveResult(entry, field, parseFloat(this.editValue));
    }
    this.editingCell = null;
    this.editValue = '';
  }

  // Notes editing
  startEditNotes(entry: EventScheduleEntryResponse) {
    if (entry.status === 'scratched' || entry.entry_type === 'break') return;
    if (!entry.registration?.class_registration_id) return;

    this.editNotesValue = this.getNotes(entry) || '';
    this.editingNotesId = entry.registration.class_registration_id;
  }

  isEditingNotes(entry: EventScheduleEntryResponse): boolean {
    if (!entry.registration?.class_registration_id) return false;
    return this.editingNotesId === entry.registration.class_registration_id;
  }

  onNotesKeydown(event: KeyboardEvent, entry: EventScheduleEntryResponse) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveNotes(entry);
    } else if (event.key === 'Escape') {
      this.editingNotesId = null;
      this.editNotesValue = '';
    }
  }

  onNotesBlur(entry: EventScheduleEntryResponse) {
    this.saveNotes(entry);
  }

  async saveNotes(entry: EventScheduleEntryResponse) {
    const registrationId = entry.registration?.class_registration_id;
    if (!registrationId || !this.selectedRound) {
      this.editingNotesId = null;
      this.editNotesValue = '';
      return;
    }

    this.processing = true;

    try {
      const existingResult = this.allResults.get(registrationId);

      if (existingResult) {
        await firstValueFrom(
          this.scoringService.updateResult({
            result_id: existingResult.result_id,
            notes: this.editNotesValue
          })
        );
      } else {
        await firstValueFrom(
          this.scoringService.createResult({
            class_registration_id: registrationId,
            round_id: this.selectedRound.round_id,
            schedule_entry_id: entry.entry_id,
            status: 'recorded',
            notes: this.editNotesValue
          })
        );
      }

      await this.loadAllResults();
    } catch (error) {
      console.error('Error saving notes:', error);
      this.toastService.error(this.translateService.instant('results.saveError'));
    } finally {
      this.processing = false;
      this.editingNotesId = null;
      this.editNotesValue = '';
    }
  }

  getNotes(entry: EventScheduleEntryResponse): string | null {
    const result = this.getResult(entry);
    return result?.notes || null;
  }

  // Image handling
  getResultImage(entry: EventScheduleEntryResponse): string | null {
    const result = this.getResult(entry);
    if (result?.images && result.images.length > 0) {
      return result.images[0].image_url;
    }
    return null;
  }

  getResultImageId(entry: EventScheduleEntryResponse): number | null {
    const result = this.getResult(entry);
    if (result?.images && result.images.length > 0) {
      return result.images[0].image_id;
    }
    return null;
  }

  openImageUpload(entry: EventScheduleEntryResponse) {
    this.currentImageEntry = entry;
    // Find the file input in the row and trigger click
    const inputs = document.querySelectorAll('.hidden-file-input');
    const index = this.participantEntries.findIndex(e => e.entry_id === entry.entry_id);
    if (index >= 0 && inputs[index]) {
      (inputs[index] as HTMLInputElement).click();
    }
  }

  async onImageSelected(event: Event, entry: EventScheduleEntryResponse) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const registrationId = entry.registration?.class_registration_id;
    if (!registrationId || !this.selectedRound) return;

    this.processing = true;

    try {
      let existingResult = this.allResults.get(registrationId);

      // Create result first if it doesn't exist
      if (!existingResult) {
        existingResult = await firstValueFrom(
          this.scoringService.createResult({
            class_registration_id: registrationId,
            round_id: this.selectedRound.round_id,
            schedule_entry_id: entry.entry_id,
            status: 'recorded'
          })
        );
      }

      // Upload image using scoring service
      if (existingResult) {
        await firstValueFrom(
          this.scoringService.uploadResultImage({
            result_id: existingResult.result_id,
            image: file,
            image_name: file.name
          })
        );
      }

      await this.loadAllResults();
      this.toastService.success(this.translateService.instant('liveSession.imageUploaded'));
    } catch (error) {
      console.error('Error uploading image:', error);
      this.toastService.error(this.translateService.instant('liveSession.imageUploadError'));
    } finally {
      this.processing = false;
      input.value = '';
    }
  }

  async removeImage(entry: EventScheduleEntryResponse) {
    const imageId = this.getResultImageId(entry);
    if (!imageId) return;

    this.processing = true;

    try {
      await firstValueFrom(
        this.scoringService.deleteResultImage({ image_id: imageId })
      );

      await this.loadAllResults();
    } catch (error) {
      console.error('Error removing image:', error);
      this.toastService.error(this.translateService.instant('results.saveError'));
    } finally {
      this.processing = false;
    }
  }

  viewImage(entry: EventScheduleEntryResponse) {
    const imageUrl = this.getResultImage(entry);
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  async saveQuickEntry() {
    if (!this.quickEntryValue.trim() || !this.currentEntry) return;

    const value = parseFloat(this.quickEntryValue);
    if (isNaN(value) || value <= 0) return;

    const field = this.isTimeBased ? 'time' : 'points';
    await this.saveResult(this.currentEntry, field, value);

    // Clear and move to next
    this.quickEntryValue = '';
    if (this.canGoNext) {
      await this.goNext();
    }
  }

  handleResultKeydown(event: KeyboardEvent, entry: EventScheduleEntryResponse, field: 'time' | 'points') {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const value = (event.target as HTMLInputElement).value;
      this.saveAndMoveToNext(entry, field, value);
    } else if (event.key === 'Escape') {
      this.editingCell = null;
    }
  }

  async saveAndMoveToNext(entry: EventScheduleEntryResponse, field: 'time' | 'points', value: string) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      await this.saveResult(entry, field, numValue);
    }

    // Find next participant (skip breaks and scratched)
    const currentIndex = this.participantEntries.findIndex(e => e.entry_id === entry.entry_id);
    let nextIndex = currentIndex + 1;

    while (nextIndex < this.participantEntries.length) {
      const nextEntry = this.participantEntries[nextIndex];
      if (nextEntry.status !== 'scratched' && nextEntry.entry_type !== 'break') {
        this.startEdit(nextEntry, field);
        return;
      }
      nextIndex++;
    }

    this.editingCell = null;
  }

  async saveResult(entry: EventScheduleEntryResponse, field: 'time' | 'points', value: number) {
    const registrationId = entry.registration?.class_registration_id;
    if (!registrationId || !this.selectedRound) return;

    this.processing = true;
    try {
      const existingResult = this.allResults.get(registrationId);

      if (existingResult) {
        // Update existing result
        await firstValueFrom(
          this.scoringService.updateResult({
            result_id: existingResult.result_id,
            ...(field === 'time' ? { raw_time_seconds: value } : { raw_points: value })
          })
        );
      } else {
        // Create new result
        await firstValueFrom(
          this.scoringService.createResult({
            class_registration_id: registrationId,
            round_id: this.selectedRound.round_id,
            schedule_entry_id: entry.entry_id,
            status: 'recorded',
            ...(field === 'time' ? { raw_time_seconds: value } : { raw_points: value })
          })
        );
      }

      // Reload results and recalculate positions
      await this.loadAllResults();
    } catch (error) {
      console.error('Error saving result:', error);
      this.toastService.error(this.translateService.instant('results.saveError'));
    } finally {
      this.processing = false;
    }
  }

  async setResultStatus(entry: EventScheduleEntryResponse, status: 'dnf' | 'dns' | 'dq') {
    const registrationId = entry.registration?.class_registration_id;
    if (!registrationId || !this.selectedRound) return;

    this.processing = true;
    try {
      const existingResult = this.allResults.get(registrationId);
      const currentStatus = existingResult?.status;

      // Toggle: if same status, clear it (set to recorded without time)
      const newStatus = currentStatus === status ? 'recorded' : status;

      if (existingResult) {
        await firstValueFrom(
          this.scoringService.updateResult({
            result_id: existingResult.result_id,
            status: newStatus,
            // Clear time/points when setting special status
            ...(newStatus !== 'recorded' ? { raw_time_seconds: undefined, raw_points: undefined } : {})
          })
        );
      } else {
        await firstValueFrom(
          this.scoringService.createResult({
            class_registration_id: registrationId,
            round_id: this.selectedRound.round_id,
            schedule_entry_id: entry.entry_id,
            status: newStatus
          })
        );
      }

      await this.loadAllResults();
    } catch (error) {
      console.error('Error setting result status:', error);
      this.toastService.error(this.translateService.instant('results.saveError'));
    } finally {
      this.processing = false;
    }
  }

  // Results Board getters
  get participantEntries(): EventScheduleEntryResponse[] {
    return this.entries.filter(e => e.entry_type === 'participant');
  }

  get completedCount(): number {
    return this.participantEntries.filter(e => {
      const regId = e.registration?.class_registration_id;
      if (!regId) return false;
      const result = this.allResults.get(regId);
      return result && result.status !== 'pending';
    }).length;
  }

  get isTimeBased(): boolean {
    return this.scoringConfig?.score_type === 'timed' || this.scoringConfig?.score_type === 'combined';
  }

  get isPointsBased(): boolean {
    return this.scoringConfig?.score_type === 'points_high' ||
           this.scoringConfig?.score_type === 'points_low' ||
           this.scoringConfig?.score_type === 'combined';
  }

  getPosition(entry: EventScheduleEntryResponse): number | null {
    const regId = entry.registration?.class_registration_id;
    if (!regId) return null;
    return this.positions.get(regId) || null;
  }

  getResult(entry: EventScheduleEntryResponse): ClassResultResponse | null {
    const regId = entry.registration?.class_registration_id;
    if (!regId) return null;
    return this.allResults.get(regId) || null;
  }

  getRawTime(entry: EventScheduleEntryResponse): number | null {
    const result = this.getResult(entry);
    return result?.raw_time_seconds || null;
  }

  getRawPoints(entry: EventScheduleEntryResponse): number | null {
    const result = this.getResult(entry);
    return result?.raw_points || null;
  }

  getFinalValue(entry: EventScheduleEntryResponse): string {
    const result = this.getResult(entry);
    if (!result || result.status !== 'recorded') return '';

    if (this.isTimeBased && result.final_time_seconds) {
      return this.formatTime(result.final_time_seconds);
    }
    if (this.isPointsBased && result.final_points !== null && result.final_points !== undefined) {
      return result.final_points.toString();
    }
    return '';
  }

  getResultStatus(entry: EventScheduleEntryResponse): string | null {
    const result = this.getResult(entry);
    return result?.status || null;
  }

  formatTime(seconds: number | null): string {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toFixed(3).padStart(6, '0')}`;
    }
    return secs.toFixed(3);
  }

  hasResult(entry: EventScheduleEntryResponse): boolean {
    const result = this.getResult(entry);
    return result !== null && result.status !== 'pending';
  }

  get hasScoringEnabled(): boolean {
    return this.scoringConfig !== null && this.rounds.length > 0;
  }

  openResultEntryModal(entry?: EventScheduleEntryResponse) {
    const targetEntry = entry || this.currentEntry;
    if (!targetEntry?.registration || !this.selectedRound) {
      // If no round is selected, show a message
      if (!this.selectedRound && this.rounds.length === 0) {
        this.toastService.warning(this.translateService.instant('results.noRoundsConfigured'));
        return;
      }
      return;
    }

    const modalRef = this.modalService.open(ResultEntryModal, { size: 'md', centered: true });
    modalRef.componentInstance.eventClass = { event_class_id: this.schedule?.event_class_id };
    modalRef.componentInstance.registration = targetEntry.registration;
    modalRef.componentInstance.round = this.selectedRound;
    modalRef.componentInstance.scheduleEntryId = targetEntry.entry_id;

    modalRef.result.then((saved) => {
      if (saved) {
        this.toastService.success(this.translateService.instant('results.savedSuccess'));
      }
    }, () => {});
  }

  // Helper methods
  getParticipantName(entry: EventScheduleEntryResponse | null): string {
    if (!entry) return this.translateService.instant('liveSession.unknown');

    // Handle breaks
    if (entry.entry_type === 'break') {
      return entry.notes || this.translateService.instant('liveSession.break');
    }

    if (!entry.registration) return this.translateService.instant('liveSession.unknown');
    const reg = entry.registration;
    return reg.user?.full_name || `${reg.user?.first_name || ''} ${reg.user?.last_name || ''}`.trim() || this.translateService.instant('liveSession.unknown');
  }

  getHorseName(entry: EventScheduleEntryResponse | null): string {
    if (!entry || entry.entry_type === 'break') return '';
    if (!entry.registration?.horse) return '';
    return entry.registration.horse.name || '';
  }

  getBreakDuration(entry: EventScheduleEntryResponse | null): string {
    if (!entry || entry.entry_type !== 'break') return '';
    const seconds = entry.duration_seconds || 0;
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  }

  getEstimatedTime(entry: EventScheduleEntryResponse | null): string {
    // Use scheduled_time (recalculated by backend) if available, fallback to start_time
    const time = entry?.scheduled_time || entry?.start_time;
    if (!time) return '--:--';
    const date = new Date(time);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  getEntryStatus(entry: EventScheduleEntryResponse): string {
    if (!entry) return '';
    if (entry.status === 'scratched') return 'scratched';
    if (entry.status === 'completed') return 'completed';
    if (entry.entry_id === this.currentEntry?.entry_id) return 'current';
    if (entry.position < this.currentPosition) return 'completed';
    return 'pending';
  }
}
